import { useState, useCallback, useRef } from 'react';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import { getHandler } from './payments/paymentHandlerRegistry';
import './payments/handlers';

/**
 * usePaymentHandler hook.
 * Orchestrate payment routing by resolving the correct handler from the registry
 * and executing it with the appropriate context.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state object.
 * @param {object} params.env - Environment configuration.
 * @param {object} params.purchaser - Purchaser data.
 * @param {object} params.actions - Action callbacks from useQuickCheckoutActions.
 * @param {object} params.engineHooks - Engine-specific hooks (e.g., evertecPayment).
 * @param {object} [params.yapeFormRef] - Ref holding the Yape form's `{ formData, isValid }`, read at
 * click time so the handler sees what the buyer typed without a re-render per keystroke.
 * @returns {object} handlePayment function and isProcessing state.
 */
function usePaymentHandler({ purchase, env, purchaser, actions, engineHooks, yapeFormRef }) {
  const [isProcessing, setIsProcessing] = useState(false);
  // The in-flight guard is a ref, not `isProcessing`: state is read from this render's closure, so
  // two calls before the next render (a click racing a resumed payment, say) would both see `false`.
  // The state stays as the rendered loading signal.
  const processingRef = useRef(false);

  const handlePayment = useCallback(async () => {
    const {
      selectedPaymentOption,
      selectedPaymentMethod,
      cardCheckoutMode,
      paymentEngine,
      isUpdating,
    } = purchase;

    if (isUpdating || processingRef.current) return;

    actions.clearError();

    const paymentType = selectedPaymentOption || 'credit_card';

    const context = {
      selectedPaymentMethod,
      cardCheckoutMode,
      paymentEngine,
      env,
    };

    const handler = getHandler(paymentType, context);

    if (!handler) {
      errorMonitoring.notify({
        error: new Error(`No payment handler registered for: ${paymentType}`),
        info: { from: 'usePaymentHandler', paymentType, paymentEngine, token: purchase.token },
      });
      actions.onPaymentFailure();
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);
    try {
      await handler.execute({
        purchase,
        env,
        purchaser,
        engineHooks,
        yapeForm: yapeFormRef?.current,
        ...actions,
      });
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: { from: 'usePaymentHandler:execute', paymentType, token: purchase.token },
      });
      // Shown only if the failing action did not already tell the buyer something specific.
      actions.onPaymentFailure();
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  }, [purchase, env, purchaser, actions, engineHooks, yapeFormRef]);

  return {
    handlePayment,
    isProcessing,
  };
}

export default usePaymentHandler;
